import { type FC, useMemo } from 'react';

import { AccoStars } from '@vakantiesnl/components/src/atoms/AccoStars';
import { AwardLabel } from '@vakantiesnl/components/src/atoms/AwardLabel';
import { Button } from '@vakantiesnl/components/src/atoms/Button';
import { Disclaimer } from '@vakantiesnl/components/src/atoms/Disclaimer';
import { Link } from '@vakantiesnl/components/src/atoms/Link';
import { Price } from '@vakantiesnl/components/src/atoms/Price';
import { Separator } from '@vakantiesnl/components/src/atoms/Separator';
import { Typography } from '@vakantiesnl/components/src/atoms/Typography';
import { AccommodationType } from '@vakantiesnl/components/src/molecules/AccommodationType';
import { LocationBreadCrumbs } from '@vakantiesnl/components/src/molecules/LocationBreadCrumbs';
import { OfferItem } from '@vakantiesnl/components/src/molecules/OfferDetails/OfferItem';
import { RatingBox } from '@vakantiesnl/components/src/molecules/RatingBox';
import { ZooverDealBar } from '@vakantiesnl/components/src/molecules/ZooverDealBar';
import { isOfferWithZooverDeal, isZoover, parseZooverDealDate } from '@vakantiesnl/components/src/utils';
import { generateDisclaimerPriceMessage } from '@vakantiesnl/components/src/utils/disclaimerPriceMessage';
import { useBreakpoints } from '@vakantiesnl/components/src/utils/useBreakpoints';
import ImageComponent, { type ImageSizesConfig } from '@vakantiesnl/image-component';
import { type Awards, type MicroCopy, type Search } from '@vakantiesnl/types/src';
import { type Location } from '@vakantiesnl/types/src/elasticsearch/entities';
import { type LinkProps as NextLinkProps } from 'next/link';

import { useStyles } from './ProductCard.style';
import { ImagePlaceholder } from '../ImagePlaceholder';

type CardSizes = 'tiny' | 'tinyMap' | 'small' | 'large';

export type ProductCardProps = {
	/** Accommodation Name */
	accoName: string;
	/** Link to the accommodation */
	href?: NextLinkProps['href'];
	/** Zoover last year award of the accommodation */
	award?: Awards.Award;
	/** Text about the offer departure */
	departureDateText?: string;
	/** Text about the offer details */
	offerSummaryText?: string;
	/** Text about the offer details */
	ctaButtonText: string;
	/** Main image to display */
	image?: string;
	/** Stars of the accommodation */
	starRating: number;
	/** Acco type */
	accoType: string;
	/** Geo Location object of the accommodation */
	location: Location[];
	/** Offer item */
	offer?: Search.OfferItem | Search.AccommodationOnlyOffer | null;
	/** Size of the card to support multiple variants across the website */
	size?: CardSizes;
	/** Microcopies from contentful */
	microCopies: MicroCopy;
	/** Rating of the accommodation */
	rating?: number;
	/** Accommodation card onClick tracking event */
	trackAccoClick?: () => void;
	onCardClick?: () => void;
	/** To open the link in the same tab */
	openInSameTab?: boolean;
	/** Tag to show above the content, used for myclient only */
	tag?: JSX.Element;
	/** Wether the acco is saved in the favorites */
	isFavorite?: boolean;
	onFavoriteClick?: () => void;
};

const imageSizes: ImageSizesConfig<CardSizes> = {
	tiny: {
		imageWidth: '288px',
		trimming: [288, 216],
	},
	tinyMap: {
		imageWidth: '288px',
		trimming: [288, 216],
	},
	small: {
		imageWidth: '375px',
		trimming: [375, 281],
	},
	large: {
		imageWidth: '320px',
		trimming: [320, 240],
	},
};

export const ProductCard: FC<ProductCardProps> = ({
	accoName,
	href,
	award,
	departureDateText,
	offerSummaryText,
	image,
	starRating,
	accoType,
	location,
	offer,
	size = 'small',
	rating,
	microCopies,
	trackAccoClick,
	onCardClick,
	ctaButtonText,
	openInSameTab,
	tag,
	isFavorite,
	onFavoriteClick,
}) => {
	const { classes, cx } = useStyles();
	const { isTabletOrIsDesktop } = useBreakpoints();

	const hasZooverDeal = isZoover && offer && isOfferWithZooverDeal(offer);

	const dataCyVariant = useMemo(() => {
		if (hasZooverDeal) return 'productCard-flashdeal';
		else if (offer) return 'productCard-offer';
		else return 'productCard-nonBookable';
	}, [hasZooverDeal, offer]);

	return (
		<div
			className={cx(classes.productCardContainer, {
				[classes.large]: size === 'large' && isTabletOrIsDesktop,
				[classes.tiny]: size === 'tiny' || size === 'tinyMap',
				[classes.map]: size === 'tinyMap',
			})}
			data-cy="productCard"
			data-cy-variant={dataCyVariant}
		>
			<div className={classes.figure}>
				<div className={classes.imageContainer}>
					{!!image ? (
						<ImageComponent
							alt={accoName}
							src={image}
							className={classes.image}
							fill={true}
							sizes={`(max-width: 768px) 100vw, 375px`}
							trim={imageSizes[size].trimming}
							priority={false}
						/>
					) : (
						<ImagePlaceholder placeholder={microCopies['gallery.no-images.text']} size="small" />
					)}

					{!!award && isZoover && <AwardLabel awardType={award.awardType} />}
					{(!isTabletOrIsDesktop || size !== 'large') && offer && (
						<>
							<div className={classes.gradient} />
							<Price
								price={offer.pricePerAdult}
								prefix={microCopies['common.perPersonFrom']}
								priceWithoutDiscount={
									'originalPricePerAdult' in offer ? offer.originalPricePerAdult : undefined
								}
								className={cx(classes.price, { [classes.zooverDealPrice]: hasZooverDeal })}
								primaryColor={false}
								size="medium"
							/>
						</>
					)}
					{isZoover && (
						<Button
							leadingIcon={isFavorite ? 'favoriteFilled' : 'favorite'}
							className={cx(classes.favoriteButton, { [classes.favoriteButtonActive]: isFavorite })}
							variant="secondary"
							size="small"
							onClick={onFavoriteClick}
						/>
					)}
					{hasZooverDeal && (
						<ZooverDealBar
							title={microCopies['accoCard.flashdeal']}
							expirationDate={parseZooverDealDate(offer.expirationDate)}
							discountAmount={offer.discountAmount}
							discountType={offer.discountAmountType}
							className={classes.zooverDeal}
							variant="squared"
						/>
					)}
				</div>
				<div className={classes.contentContainer}>
					{!!tag && tag}
					<div className={classes.content}>
						<div className={classes.mainInfo}>
							<div className={classes.heading}>
								<Typography
									as="h2"
									variant={size === 'large' && isTabletOrIsDesktop ? 'headingMd' : 'headingXs'}
									className={cx(classes.headingTitle, { [classes.headingTitleFullWidth]: !rating })}
									data-cy="productCard-title"
								>
									{accoName}
								</Typography>
								{!!rating && rating !== 0 && (
									<div className={classes.ratingBox}>
										<RatingBox
											rating={rating}
											size={isTabletOrIsDesktop ? 'medium' : 'small'}
											microCopies={microCopies}
											label={false}
										/>
									</div>
								)}
							</div>
							<div className={classes.infoItem}>
								<AccommodationType
									accoType={accoType}
									icon="accommodation"
									iconSize="medium"
									className={classes.accoType}
								/>
								{!!starRating && (
									<>
										<Separator />
										<AccoStars starsCount={starRating} size="xSmall" />
									</>
								)}
							</div>
							{location && (
								<div>
									<LocationBreadCrumbs
										location={location}
										breadCrumbClassName={classes.breadCrumbs}
										noLinks
									/>
								</div>
							)}
							{departureDateText && size !== 'tiny' && (
								<OfferItem
									variant="bold"
									iconType="calendar"
									title={departureDateText}
									dataCy="OfferSummary-duration"
								/>
							)}
							{!!offerSummaryText && size !== 'tiny' && (
								<Typography
									variant="labelXs"
									as="p"
									className={classes.offerIncludesMessage}
									data-cy="OfferSummary-text"
								>
									{offerSummaryText}
								</Typography>
							)}
						</div>
					</div>
					{(!!offer || !!href || !!onCardClick) && (
						<div className={classes.details}>
							<div className={classes.offerWrapper}>
								{!!offer && (
									<div className={classes.pricewrapper}>
										{((isTabletOrIsDesktop && size === 'large') ||
											(!isTabletOrIsDesktop && size === 'tinyMap')) && (
											<Price
												price={offer.pricePerAdult}
												prefix={microCopies['common.perPersonFrom']}
												priceWithoutDiscount={
													'originalPricePerAdult' in offer
														? offer.originalPricePerAdult
														: undefined
												}
												className={classes.priceLarge}
												primaryColor={false}
												size="medium"
											/>
										)}
										{offer?.extras && (
											<div>
												<Disclaimer
													textAlign={isTabletOrIsDesktop ? 'right' : 'left'}
													className={classes.disclaimer}
												>
													{generateDisclaimerPriceMessage(offer.extras, microCopies)}
												</Disclaimer>
											</div>
										)}
									</div>
								)}
								{(!!href || !!onCardClick) && (
									<>
										{href && (
											<Link
												href={createHrefWithHash(href)}
												target={openInSameTab ? '_self' : '_blank'}
												className={classes.link}
												title={accoName}
												data-cy="accommodationCard-link"
												onClick={trackAccoClick}
											>
												<span>{microCopies['accommodation.linkLabel']}</span>
											</Link>
										)}
										{(size !== 'tiny' || !!onCardClick) && (
											<Button
												size="small"
												className={classes.button}
												onClick={onCardClick}
												asText={onCardClick ? undefined : true}
											>
												{ctaButtonText}
											</Button>
										)}
									</>
								)}
							</div>
						</div>
					)}
				</div>
			</div>
		</div>
	);
};

function createHrefWithHash(href: NextLinkProps['href']): string {
	if (typeof href === 'string') {
		return href;
	}

	const query = href.query?.toString();

	if (!query) {
		return href.pathname || '';
	}

	return `${href.pathname}#${query}`;
}
